import React from 'react';
import Loading from './components/loading';
import Loadable from 'react-loadable';
import { Redirect } from 'react-router-dom';
import { getSession } from './store/session/selectors';
import { OffCanvasItem } from './components/offCanvasNavigation';
import { allProfileTypes } from './constants/profileTypes';
import StaffNavItem from './modules/staff/navItem';
import { STAFF_PATH } from './modules/staff/tabs';
import FinesNavItem from './modules/fines/navItem';
import HelpNavItem from './modules/help/navItem';

/** Old staff paths now live in the Keenvil Staff tabs. */
const staffRedirect = tab => () => <Redirect to={`${STAFF_PATH}/${tab}`} />;

const adminLoadables = {
  Users: Loadable({
    loader: () => import('./modules/users'),
    loading() {
      return <Loading legend="Cargando usuarios" />;
    }
  }),
  Units: Loadable({
    loader: () => import('./modules/units'),
    loading() {
      return <Loading legend="Cargando unidades" />;
    }
  }),
  Vehicles: Loadable({
    loader: () => import('./modules/vehicles'),
    loading() {
      return <Loading legend="Cargando vehículos" />;
    }
  }),
  Communication: Loadable({
    loader: () => import('./modules/communication'),
    loading() {
      return <Loading legend="Cargando comunicaciones" />;
    }
  }),
  Movements: Loadable({
    loader: () => import('./modules/movements'),
    loading() {
      return <Loading legend="Cargando movimientos" />;
    }
  }),
  Amenities: Loadable({
    loader: () => import('./modules/amenities'),
    loading() {
      return <Loading legend="Cargando comodidades" />;
    }
  }),
  Segments: Loadable({
    loader: () => import('./modules/segments'),
    loading() {
      return <Loading legend="Cargando segmentos" />;
    }
  }),
  CriminalRecords: Loadable({
    loader: () => import('./modules/criminalRecords'),
    loading() {
      return <Loading legend="Cargando criminalrecords" />;
    }
  }),
  Inbox: Loadable({
    loader: () => import('./modules/inbox'),
    loading() {
      return <Loading legend="Cargando inbox" />;
    }
  }),
  Blacklist: Loadable({
    loader: () => import('./modules/blacklist'),
    loading() {
      return <Loading legend="Cargando blacklist" />;
    }
  }),
  Settings: Loadable({
    loader: () => import('./modules/settings'),
    loading() {
      return <Loading legend="Cargando segmentos" />;
    }
  }),
  Tutorials: Loadable({
    loader: () => import('./modules/tutorials'),
    loading() {
      return <Loading legend="Cargando tutoriales" />;
    }
  }),
  Staff: Loadable({
    loader: () => import('./modules/staff'),
    loading() {
      return <Loading legend="Cargando Keenvil Staff" />;
    }
  }),
  Fines: Loadable({
    loader: () => import('./modules/fines'),
    loading() {
      return <Loading legend="Cargando multas" />;
    }
  }),
  Help: Loadable({
    loader: () => import('./modules/help'),
    loading() {
      return <Loading legend="Cargando preguntas frecuentes" />;
    }
  })
};

const residentLoadables = {
  Home: Loadable({
    loader: () => import('./residentModules/home'),
    loading() {
      return <Loading legend="Cargando Inicio" />;
    }
  })
};

const LoadableCriminalRecords = Loadable({
  loader: () => import('./modules/criminalRecords'),
  loading() {
    return <Loading legend="Cargando criminalrecords" />;
  }
});

/**
 * This file defines the Modules Routes.
 * Do not define subroutes here. Use the Module index file
 * to do so. That way we encapsulate features. THANKS, Esteban.
 */

const adminRoutes = [
  {
    name: 'Usuarios',
    path: '/users',
    navItem: currentPath => (
      <OffCanvasItem
        label="Usuarios"
        icon="users"
        linkTo="/users"
        isActive={currentPath.includes('users')}
      />
    ),
    component: adminLoadables.Users
  },
  {
    name: 'Unidades Funcionales',
    path: '/units',
    navItem: currentPath => (
      <OffCanvasItem
        label="Unidades Funcionales"
        icon="home"
        linkTo="/units"
        isActive={currentPath.includes('units')}
      />
    ),
    component: adminLoadables.Units
  },
  {
    name: 'Vehículos',
    path: '/vehicles',
    navItem: currentPath => (
      <OffCanvasItem
        label="Vehículos"
        icon="car"
        linkTo="/vehicles"
        isActive={currentPath.includes('vehicles')}
      />
    ),
    component: adminLoadables.Vehicles
  },
  {
    name: 'Comunicación',
    path: '/communications',
    navItem: currentPath => (
      <OffCanvasItem
        label="Comunicación"
        icon="chat"
        linkTo="/communications"
        isActive={currentPath.includes('communications')}
      />
    ),
    component: adminLoadables.Communication
  },
  {
    name: 'Movimientos',
    path: '/movements',
    navItem: currentPath => (
      <OffCanvasItem
        label="Movimientos"
        icon="arrows"
        linkTo="/movements"
        isActive={currentPath.includes('movements')}
      />
    ),
    component: adminLoadables.Movements,
    exact: true
  },
  {
    // Segmentos es una pestaña de Configuración (/settings/SEGMENTS); estas rutas quedan para alta y edición.
    name: 'Segmentos',
    path: '/segments',
    component: adminLoadables.Segments
  },
  {
    name: 'Amenities',
    path: '/amenities',
    navItem: currentPath => (
      <OffCanvasItem
        label="Amenities"
        icon="calendar"
        linkTo="/amenities"
        // Editar un amenity es de Configuración → Amenities, no de Reservas.
        isActive={
          currentPath.includes('amenities') &&
          !currentPath.includes('/amenities/edit')
        }
      />
    ),
    component: adminLoadables.Amenities
  },
  {
    name: 'Notificationes',
    path: '/inbox',
    component: adminLoadables.Inbox
  },
  {
    name: 'Antecedentes Penales',
    path: '/criminalrecords',
    navItem: currentPath => (
      <OffCanvasItem
        label="Antecedentes Penales"
        icon="criminal"
        linkTo="/criminalrecords"
        isActive={currentPath.includes('criminalrecords')}
      />
    ),
    component: LoadableCriminalRecords
  },
  {
    name: 'Blacklist',
    path: '/blacklist',
    navItem: currentPath => (
      <OffCanvasItem
        label="Blacklist"
        icon="blacklist"
        linkTo="/blacklist"
        isActive={currentPath.includes('blacklist')}
      />
    ),
    component: adminLoadables.Blacklist
  },
  {
    // Multas de amercement-api: actas de todos los motivos, catalogo y descargos ([MULTAS-1410]).
    name: 'Multas',
    path: '/fines',
    navItem: currentPath => (
      <FinesNavItem key="fines" currentPath={currentPath} />
    ),
    component: adminLoadables.Fines
  },
  {
    name: 'Configuración',
    path: '/settings',
    navItem: currentPath => (
      <OffCanvasItem
        label="Configuración"
        icon="settings"
        linkTo="/settings"
        isActive={
          currentPath.includes('settings') ||
          currentPath.includes('segments') ||
          currentPath.includes('/amenities/edit')
        }
      />
    ),
    component: adminLoadables.Settings
  },
  {
    name: 'Tutoriales',
    path: '/tutorials',
    component: adminLoadables.Tutorials
  },
  {
    // Base de conocimiento global ([FAQ-ZENDESK]): el item aparece si townhall-api la tiene habilitada.
    name: 'Preguntas frecuentes',
    path: '/help',
    navItem: currentPath => (
      <HelpNavItem key="help" currentPath={currentPath} />
    ),
    component: adminLoadables.Help
  },
  {
    // Solo staff de Keenvil: Comunidades y Ayuda en pestañas, con item violeta.
    name: 'Keenvil Staff',
    path: '/staff/:tab?',
    navItem: currentPath => (
      <StaffNavItem key="staff" currentPath={currentPath} />
    ),
    component: adminLoadables.Staff
  },
  {
    // Links viejos: llevan a la pestaña de Keenvil Staff.
    name: 'Comunidades',
    path: '/communities-admin',
    component: staffRedirect('COMMUNITIES')
  },
  {
    name: 'Ayuda',
    path: '/help-admin',
    component: staffRedirect('HELP')
  }
];

const supervisorRoutes = [
  {
    name: 'Movimientos',
    path: '/movements',
    navItem: currentPath => (
      <OffCanvasItem
        label="Movimientos"
        icon="arrows"
        linkTo="/movements"
        isActive={currentPath.includes('movements')}
      />
    ),
    component: adminLoadables.Movements,
    exact: true
  },
  {
    name: 'Amenities',
    path: '/amenities',
    navItem: currentPath => (
      <OffCanvasItem
        label="Amenities"
        icon="calendar"
        linkTo="/amenities"
        isActive={currentPath.includes('amenities')}
      />
    ),
    component: adminLoadables.Amenities
  },
  {
    name: 'Vehiculos',
    path: '/vehicles',
    navItem: currentPath => (
      <OffCanvasItem
        label="Vehiculos"
        icon="car"
        linkTo="/vehicles"
        isActive={currentPath.includes('vehicles')}
      />
    ),
    component: adminLoadables.Vehicles
  },
  {
    // Base de conocimiento global ([FAQ-ZENDESK]): el item aparece si townhall-api la tiene habilitada.
    name: 'Preguntas frecuentes',
    path: '/help',
    navItem: currentPath => (
      <HelpNavItem key="help" currentPath={currentPath} />
    ),
    component: adminLoadables.Help
  }
];

const residentRoutes = [
  {
    name: 'Vehículos',
    path: '/vehicles',
    navItem: currentPath => (
      <OffCanvasItem
        label="Vehículos"
        icon="car"
        linkTo="/vehicles"
        isActive={currentPath.includes('vehicles')}
      />
    ),
    component: adminLoadables.Vehicles
  },
  {
    // Base de conocimiento global ([FAQ-ZENDESK]): el item aparece si townhall-api la tiene habilitada.
    name: 'Preguntas frecuentes',
    path: '/help',
    navItem: currentPath => (
      <HelpNavItem key="help" currentPath={currentPath} />
    ),
    component: adminLoadables.Help
  }
];

export default function(state) {
  switch (getSession(state).type) {
    case allProfileTypes.ADMIN:
      return adminRoutes;
    case allProfileTypes.SUPERVISOR:
      return supervisorRoutes;
    case allProfileTypes.RESIDENT:
      return residentRoutes;
  }
}
